import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { useAuth } from '@/shared/auth/useAuth'
import { usePermissions } from '@/shared/auth/usePermissions'
import { useBranchChoices } from './branchApi'
import { ActiveBranchesContext } from './useActiveBranches'

const storageKey = (userId: string) => `xsib.activeBranches.${userId}`

function readStored(userId: string | undefined): string[] {
  if (!userId) return []
  try {
    const raw = localStorage.getItem(storageKey(userId))
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : []
  } catch {
    return []
  }
}

/**
 * Sarlavhadagi filial tanlovi (ADR-046): ko'rinishni foydalanuvchining ruxsat etilgan filiallari ichida
 * toraytiradi. Ruxsatni server baribir tokendagi doiradan tekshiradi.
 */
export function ActiveBranchesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const { can } = usePermissions()
  const choices = useBranchChoices(Boolean(user) && can('subscriber.read'))
  const [stored, setStored] = useState<string[]>(() => readStored(user?.id))
  const [owner, setOwner] = useState(user?.id)
  if (owner !== user?.id) {
    setOwner(user?.id)
    setStored(readStored(user?.id))
  }

  const options = useMemo(() => choices.data ?? [], [choices.data])
  const selected = useMemo(() => stored.filter((id) => options.some((o) => o.id === id)), [stored, options])

  const setSelected = useCallback(
    (ids: readonly string[]) => {
      setStored([...ids])
      if (!user?.id) return
      try {
        localStorage.setItem(storageKey(user.id), JSON.stringify(ids))
      } catch {
        // brauzer saqlashni rad etsa tanlov faqat shu seansda qoladi
      }
    },
    [user?.id],
  )

  const value = useMemo(() => ({ options, selected, setSelected }), [options, selected, setSelected])
  return <ActiveBranchesContext.Provider value={value}>{children}</ActiveBranchesContext.Provider>
}
